import { toInteger, flatten, debounce, isNil, isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { FinanceType, Condition } from '@upstart/harmony/lib/legos/constants';
import { Pair } from '@upstart/harmony/lib/legos/creditPricingType';
import {
  CREDIT_RATING_MAX,
  CREDIT_RATING_MIN,
  ORDERED_TIERS,
  MAX_LEASE_MILEAGES,
  ORDERED_LEASE_TERMS,
  ORDERED_MILEAGES,
  ORDERED_PURCHASE_TERMS,
} from './constants';
import {
  Tier,
  TieredAprConfig,
  PurchasePricingValues,
  LeasePricingTierTermValue,
} from './types';

export type TierValidateProps = {
  previous?: Tier;
  value: Tier;
  next?: Tier;
};

export type TierValidator = {
  key: string;
  error: string;
  validation: (props: TierValidateProps) => string[];
};

export type AprConfigValidatorProps = {
  validators?: TierValidator[];
  condition: Condition;
  aprConfig?: TieredAprConfig;
  onePayLeaseEnabled?: boolean;
};

export type AprConfigValidationResult = {
  success: boolean;
  errors: {
    [index in string]: string[];
  };
};

/**
 *
 * @param trigger
 * condition which must evaluate to true or else rule will not invoke
 * @param rule
 * bool func which must return false for offenders to return
 * @param offenders
 * keys of tiers which failed based on rule failing
 */
const buildValidationResult = ({
  trigger,
  rule,
  offenders,
}: {
  trigger: boolean;
  rule: () => boolean;
  offenders: string[];
}) => (!trigger || rule() ? [] : offenders);

/**
 *
 * @param value
 * @returns
 * Append `.high` to tier key name (tier1, tier2)
 * @example
 * low(value) => tier2.low
 */
const high = (value?: Tier) =>
  value?.key ? [value.key, 'high'].join('.') : '';
/**
 *
 * @param value
 * @returns
 * Append `.low` to tier key name (tier1, tier2)
 * @example
 * low(value) => tier2.low
 */

const low = (value?: Tier) => (value?.key ? [value.key, 'low'].join('.') : '');

/**
 * List of default validators that are run against tier definitions
 */
export const defaultValidators: TierValidator[] = [
  {
    key: 'isFullyDefined1',
    error: 'low must be defined',
    validation: ({ value }: TierValidateProps) =>
      buildValidationResult({
        trigger: true,
        rule: () => !!value.low,
        offenders: [low(value)],
      }),
  },
  {
    key: 'isFullyDefined2',
    error: 'high must be defined',
    validation: ({ value }: TierValidateProps) =>
      buildValidationResult({
        trigger: true,
        rule: () => !!value.high,
        offenders: [high(value)],
      }),
  },
  {
    key: 'isLowLessThanHigh',
    error: 'low must be less than high',
    validation: ({ value }: TierValidateProps) =>
      buildValidationResult({
        trigger: !!value.low && !!value.high,
        rule: () => toInteger(value.low) < toInteger(value.high),
        offenders: [low(value)],
      }),
  },
  {
    key: 'isSortedDescending1',
    error: 'credit tiers may not have gaps',
    validation: ({ previous, value }: TierValidateProps) =>
      buildValidationResult({
        trigger:
          !!previous?.low &&
          !!value.high &&
          toInteger(value.high) < toInteger(previous?.low as string),
        rule: () =>
          toInteger(value.high) === toInteger(previous?.low as string) - 1,
        offenders: [low(previous), high(value)],
      }),
  },
  {
    key: 'isSortedDescending2',
    error: 'credit tiers may not overlap',
    validation: ({ previous, value }: TierValidateProps) =>
      buildValidationResult({
        trigger: !!previous?.low,
        rule: () => toInteger(value.high) < toInteger(previous?.low as string),
        offenders: [low(previous), high(value)],
      }),
  },
  {
    key: 'isDigitsOnly1',
    error: 'low must be an integer only',
    validation: ({ value }: TierValidateProps) =>
      buildValidationResult({
        trigger: true,
        rule: () => typeof parseInt(value.low, 10) === 'number',
        offenders: [low(value)],
      }),
  },
  {
    key: 'isDigitsOnly2',
    error: 'high must be an integer only',
    validation: ({ value }: TierValidateProps) =>
      buildValidationResult({
        trigger: true,
        rule: () => typeof parseInt(value.high, 10) === 'number',
        offenders: [high(value)],
      }),
  },
  {
    key: 'isWithinBounds1',
    error: `low must be within (${CREDIT_RATING_MAX} - ${CREDIT_RATING_MIN})`,
    validation: ({ value }: TierValidateProps) => {
      const isInsideBounds = (v: number, min: number, max: number) =>
        v >= min && v <= max;
      return buildValidationResult({
        trigger: !!value.low,
        rule: () =>
          isInsideBounds(
            toInteger(value.low),
            CREDIT_RATING_MIN,
            CREDIT_RATING_MAX,
          ),
        offenders: [low(value)],
      });
    },
  },
  {
    key: 'isWithinBounds2',
    error: `high must be within (${CREDIT_RATING_MIN} - ${CREDIT_RATING_MAX})`,
    validation: ({ value }: TierValidateProps) => {
      const isInsideBounds = (v: number, min: number, max: number) =>
        v >= min && v <= max;
      return buildValidationResult({
        trigger: !!value.high,
        rule: () =>
          isInsideBounds(
            toInteger(value.high),
            CREDIT_RATING_MIN,
            CREDIT_RATING_MAX,
          ),
        offenders: [high(value)],
      });
    },
  },
];

export type ValidateTier = {
  key: string;
  values: Tier[];
};

const mergeMaps = <T, K>(...maps: Map<T, K>[]) =>
  new Map<T, K>(flatten(maps.map((m) => Array.from(m.entries()))));

/**
 * Builds key for searching in error map dictionary
 */
export const getValidationKey = ({
  financeType,
  tierIndex,
  termIndex,
  mileageIndex,
  suffix,
}: {
  financeType: FinanceType;
  tierIndex?: number;
  termIndex?: number;
  mileageIndex?: number;
  suffix?: string;
}) => {
  // string concat to build validation key
  const concat: string[] = [financeType];

  if (!isNil(termIndex))
    concat.push(
      financeType === FinanceType.PURCHASE
        ? ORDERED_PURCHASE_TERMS[termIndex]
        : ORDERED_LEASE_TERMS[termIndex],
    );

  if (!isNil(tierIndex)) concat.push(ORDERED_TIERS[tierIndex]);

  if (!isNil(mileageIndex)) concat.push(ORDERED_MILEAGES[mileageIndex]);

  if (!isNil(suffix)) concat.push(suffix);

  return concat.join('.');
};

/**
 * Custom hook for validating apr config
 */
export function useAprConfigValidator({
  validators,
  aprConfig,
  onePayLeaseEnabled,
  condition,
}: AprConfigValidatorProps) {
  const [validationResult, setValidationResult] =
    useState<AprConfigValidationResult>({
      success: true,
      errors: {},
    });
  const validate = (config?: TieredAprConfig) => {
    if (!config) return;
    const { tiers } = config;
    // for NEW vehicles
    // mileage1,mileage2,mileage3 are required fields
    // term1.months is required field
    const errorMap = new Map<string, string[]>();

    const addFieldError = (key: string, error?: string) =>
      errorMap.set(
        key,
        error
          ? [...(errorMap?.get(key) || []), error]
          : errorMap.get(key) || [],
      );

    if (condition === Condition.NEW) {
      const [mileage1, mileage2, mileage3] =
        config.pricing.lease.terms?.[0]?.mileages || [];
      if (isEmpty(mileage1))
        addFieldError(
          getValidationKey({
            financeType: FinanceType.LEASE,
            termIndex: 0,
            mileageIndex: 0,
          }),
          'lease mileage 1 is required',
        );
      if (isEmpty(mileage2))
        addFieldError(
          getValidationKey({
            financeType: FinanceType.LEASE,
            termIndex: 0,
            mileageIndex: 1,
          }),
          'lease mileage 2 is required',
        );
      if (isEmpty(mileage3))
        addFieldError(
          getValidationKey({
            financeType: FinanceType.LEASE,
            termIndex: 0,
            mileageIndex: 2,
          }),
          'lease mileage 3 is required',
        );
      if (isEmpty(config.pricing.lease.terms?.[0]?.months))
        addFieldError(
          getValidationKey({
            financeType: FinanceType.LEASE,
            termIndex: 0,
            suffix: 'months',
          }),
          'lease term 1 length is required',
        );
    }

    const duplicateMileages = new Set<string>();
    config.pricing.lease.terms?.[0]?.mileages.forEach((x, i) => {
      if (duplicateMileages.has(x)) {
        addFieldError(
          getValidationKey({
            financeType: FinanceType.LEASE,
            termIndex: 0,
            mileageIndex: i,
          }),
          'duplicate lease mileage',
        );
      }
      if (!isEmpty(x)) duplicateMileages.add(x);
    });

    const duplicateLeaseTerms = new Set<string>();
    config.pricing.lease.terms.forEach((x, i) => {
      if (duplicateLeaseTerms.has(x.months)) {
        addFieldError(
          getValidationKey({
            financeType: FinanceType.LEASE,
            termIndex: i,
            suffix: 'months',
          }),
          'duplicate lease term',
        );
      }
      if (!isEmpty(x.months)) duplicateLeaseTerms.add(x.months);
    });

    const duplicatePurchaseTerms = new Set<string>();
    config.pricing.purchase.terms?.forEach((x, i) => {
      if (duplicatePurchaseTerms.has(x)) {
        addFieldError(
          getValidationKey({
            financeType: FinanceType.PURCHASE,
            termIndex: i,
            suffix: 'months',
          }),
        );
      }
      duplicatePurchaseTerms.add(x);
    });

    const validatePricingMatrixMap = validatePricingMatrix(config);
    const validatePurchaseTiers = validateTierDefinitions({
      key: FinanceType.PURCHASE,
      values: tiers.purchase || [],
    });
    const validateLeaseTiers =
      condition === Condition.NEW
        ? validateTierDefinitions({
            key: FinanceType.LEASE,
            values: tiers.lease || [],
          })
        : new Map();

    const mergedMap = mergeMaps(
      errorMap,
      validatePricingMatrixMap,
      validatePurchaseTiers,
      validateLeaseTiers,
    );

    const result = {
      success: mergedMap.size === 0,
      errors:
        mergedMap.size === 0
          ? {}
          : Array.from(mergedMap.entries()).reduce(
              (accumulator, [key, value]) => {
                accumulator[key] = value || [];
                return accumulator;
              },
              {} as { [index: string]: string[] },
            ),
    };
    setValidationResult(result);
  };

  const validateDebounce = useRef(debounce(validate, 500));

  useEffect(() => validateDebounce.current(aprConfig), [aprConfig]);

  /**
   * Runs all validators against current, previous & next tier
   * @returns
   * Hashmap of failed tiers & error messages
   * example: `[ [tier1.low, ['error 1', 'error 2']], [tier2.high, ['error 1']] ]`
   */
  const validateNeighbouringTiers = ({
    value,
    previous,
    next,
  }: {
    value: Tier;
    previous?: Tier;
    next?: Tier;
  }) => {
    const initialValue: Pair<string, string>[] = [];
    return (
      validators
        ?.map(
          (validator): Pair<string[], string> => [
            validator.validation({ previous, value, next }),
            validator.error,
          ],
        )
        .filter((v) => v[0].length > 0)
        // only care about the validations that failed
        .reduce((acc, [keys, error]) => {
          keys.forEach((k) => acc.push([k, error]));
          return acc;
        }, initialValue) || []
    );
  };

  /**
   * Validates lease & purchase pricing w.r.t tier definition contracts
   * @returns map of tier keys and error descriptions
   */
  const validatePricingMatrix = (
    config?: TieredAprConfig,
  ): Map<string, string[]> => {
    const { pricing: pricingMatrix } = config || {};

    const errors = new Map<string, string[]>();

    // validate purchase pricing matrix
    const purchase = pricingMatrix?.[FinanceType.PURCHASE];

    type purchasePricingValuesKey = keyof PurchasePricingValues;
    const markupValue: purchasePricingValuesKey = 'markup';

    purchase?.pricing.forEach((row, rowIndex) => {
      purchase?.terms.forEach((col, colIndex) => {
        col &&
          Object.entries(row[colIndex]).forEach(([key, value]) => {
            // if addOEMMarkupRate is disabled, then we should forgo this validation
            if (!config?.options.addOEMMarkupRate && key === markupValue)
              return;

            if (isEmpty(value)) {
              const errorKey = getValidationKey({
                financeType: FinanceType.PURCHASE,
                termIndex: colIndex,
                tierIndex: rowIndex,
                suffix: key,
              });
              errors.set(errorKey, []);
            }
          });
      });
    });

    // validate lease pricing matrix
    const lease = pricingMatrix?.[FinanceType.LEASE];

    type LeasePricingTierTermValueKey = keyof LeasePricingTierTermValue;
    const onePayLeaseMoneyFactor: LeasePricingTierTermValueKey =
      'onePayLeaseMoneyFactor';

    lease?.pricing.forEach((rows, rowIndex) => {
      lease?.terms.forEach(({ months }, colIndex) => {
        const mileages = lease.terms[0].mileages;
        if (months) {
          const pageSize = MAX_LEASE_MILEAGES;
          const start = colIndex * pageSize;
          const end = start + pageSize;

          const invalidNumber = (number: string) =>
            Number.isNaN(Number(number));
          rows.slice(start, end).forEach((values, mileageIndex) => {
            const mileageExists = mileages[mileageIndex];
            mileageExists &&
              Object.entries(values).forEach(([key, value]) => {
                // if one pay lease is disabled, then we should forgo this validation
                if (!onePayLeaseEnabled && key === onePayLeaseMoneyFactor)
                  return;

                if (invalidNumber(value) || isEmpty(value)) {
                  const errorKey = getValidationKey({
                    financeType: FinanceType.LEASE,
                    termIndex: colIndex,
                    tierIndex: rowIndex,
                    mileageIndex,
                    suffix: key,
                  });
                  errors.set(errorKey, []);
                }
              });
          });
        }
      });
    });
    return errors;
  };

  /**
   * Runs validations against tiers
   * @returns map of error keys and error descriptions
   * @example
   * {
   *   success: false,
   *   errors: {
   *       'lease.tier2.low' : ['error one', 'error two'],
   *       'purchase.tier9.low' : ['error one', 'error two'],
   *   }
   * }
   */
  const validateTierDefinitions = ({
    key, // lease || purchase
    values,
  }: {
    key: string;
    values: Tier[];
  }) => {
    const errorMap = new Map<string, string[]>();

    // populate keys inside tier type (for validation): tier1, tier2, tier3 ...etc
    // not populated by default
    values.forEach((value, index) => {
      const compositeKey = [key, `tier${index + 1}`, 'displayName'].join('.');

      if (index < ORDERED_TIERS.length) value.key = ORDERED_TIERS[index];
      if (
        value &&
        typeof value.displayName === 'string' &&
        value.displayName.length > 12
      ) {
        if (!errorMap.has(compositeKey)) {
          errorMap.set(compositeKey, []);
        }
        errorMap
          .get(compositeKey)
          ?.push('Tier Name need to be under 12 characters');
      }
      if (
        value.displayName &&
        values.filter(
          (e) => e.displayName?.trim() === value.displayName?.trim(),
        ).length > 1
      ) {
        if (!errorMap.has(compositeKey)) {
          errorMap.set(compositeKey, []);
        }
        errorMap.get(compositeKey)?.push('Tier names must be unique');
      }
    });
    const missingIndex: number[] = [];
    const newArray = values.filter((e, index) => {
      if (e.displayName) {
        return e.displayName;
      }
      missingIndex.push(index);
      return false;
    });
    if (newArray.length !== 0 && newArray.length !== values.length) {
      missingIndex.forEach((index) => {
        const compositeKey = [key, `tier${index + 1}`, 'displayName'].join('.');

        if (!errorMap.has(compositeKey)) {
          errorMap.set(compositeKey, []);
        }
        errorMap.get(compositeKey)?.push('Add credit tier name to all labels');
      });
    }
    values.forEach((value, index) => {
      const errors = validateNeighbouringTiers({
        value,
        previous: values?.[index - 1],
        next: values?.[index + 1],
      });

      errors?.forEach(([errkey, errDescription]) => {
        const compositeKey = [key, errkey].join('.');
        if (!errorMap.has(compositeKey)) {
          errorMap.set(compositeKey, []);
        }
        errorMap.get(compositeKey)?.push(errDescription);
      });
    });

    return errorMap;
  };

  return { validationResult };
}
